/**
 * Shared benefit-redeem card (Fenton 2026-09-09): full state machine in
 * one component, used both on the venue home (inline redeem, no
 * click-through) and on the /benefits page (per-benefit row).
 *
 * States:
 *   idle    - shows a Redeem button
 *   posting - button disabled while the mutation flies
 *   redeemed - "Show this at the bar" proof panel + live elapsed ticker
 *              + countdown until the next anniversary rollover
 *   locked   - already-redeemed-this-period, learnt from a 400 response
 *              carrying nextAvailableAt; shows the same countdown
 *
 * Two visual variants: `variant="row"` (compact, for the /benefits list)
 * and `variant="hero"` (larger, accent-tinted, for the home surface).
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { redeemMyBenefit, RedeemBenefitError, type RedeemMyBenefitOk } from "../lib/api";
import { formatCountdown } from "../lib/redemption-period";
import { useScreenshotGuard } from "../lib/screenshot-guard";

export type RedeemableBenefit = {
  id: string;
  description: string | null;
  type: string;
  isWelcomeReward: boolean;
  redemptionLimit: number | null;
  iconEmoji?: string | null;
};

type LocalState =
  | { kind: "idle" }
  | { kind: "redeemed"; at: number; nextAvailableAt: string | null }
  | { kind: "locked"; nextAvailableAt: string | null; reason: string };

export function BenefitRedeemCard({
  venueId,
  benefit,
  label,
  icon,
  variant = "row",
}: {
  venueId: string;
  benefit: RedeemableBenefit;
  /** Fallback label (type-derived) shown under the description. */
  label: string;
  /** Small icon; passed by the caller so /benefits can supply per-type SVGs. */
  icon?: React.ReactNode;
  variant?: "row" | "hero";
}) {
  const qc = useQueryClient();
  const [state, setState] = useState<LocalState>({ kind: "idle" });

  const mutation = useMutation({
    mutationFn: () => redeemMyBenefit(venueId, benefit.id),
    onSuccess: (data: RedeemMyBenefitOk) => {
      setState({ kind: "redeemed", at: Date.now(), nextAvailableAt: data.nextAvailableAt });
      qc.invalidateQueries({ queryKey: ["benefits", venueId] });
    },
    onError: (e: Error) => {
      if (e instanceof RedeemBenefitError) {
        setState({ kind: "locked", nextAvailableAt: e.nextAvailableAt, reason: e.message });
      } else {
        setState({ kind: "locked", nextAvailableAt: null, reason: e.message });
      }
    },
  });

  const displayName = benefit.description ?? label;

  if (state.kind === "redeemed") {
    return (
      <RedeemedProof
        name={displayName}
        redeemedAt={state.at}
        variant={variant}
        onExpire={() =>
          setState({
            kind: "locked",
            nextAvailableAt: state.nextAvailableAt,
            reason: "Redeemed this period",
          })
        }
      />
    );
  }

  if (state.kind === "locked" && state.nextAvailableAt) {
    return (
      <LockedCountdown
        name={displayName}
        reason={state.reason}
        nextAvailableAt={state.nextAvailableAt}
        variant={variant}
      />
    );
  }

  if (variant === "hero") {
    return (
      <div className="flex items-center gap-3 rounded-[var(--radius)] border border-[var(--accent)]/40 bg-[var(--accent)]/10 p-4">
        <span
          aria-hidden
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-white"
        >
          {benefit.iconEmoji ? (
            <span className="text-lg">{benefit.iconEmoji}</span>
          ) : (
            <GiftIcon className="h-5 w-5" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[var(--foreground)]">
            {benefit.isWelcomeReward ? "Your welcome perk" : "Ready to claim"}
          </p>
          <p className="text-sm font-normal text-white/80">{displayName}</p>
          {state.kind === "locked" && !state.nextAvailableAt && (
            <p className="mt-1 text-xs text-amber-500">{state.reason}</p>
          )}
        </div>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="shrink-0 rounded-full bg-[var(--accent)] px-4 py-2 text-xs font-bold transition hover:opacity-90 disabled:opacity-50"
          style={{ color: "var(--accent-fg)" }}
        >
          {mutation.isPending ? "…" : "Redeem"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      {benefit.iconEmoji ? (
        <span aria-hidden className="mt-0.5 shrink-0 text-xl">
          {benefit.iconEmoji}
        </span>
      ) : (
        icon && <span className="mt-0.5 shrink-0 text-[var(--venue-accent)]">{icon}</span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[var(--subtle)]">{displayName}</p>
        <p className="text-xs text-[var(--muted)]">
          {label}
          {benefit.redemptionLimit ? ` · redeemable (${benefit.redemptionLimit}/period)` : " · redeemable"}
        </p>
        {state.kind === "locked" && !state.nextAvailableAt && (
          <p className="mt-1 text-xs text-amber-500">{state.reason}</p>
        )}
      </div>
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="shrink-0 rounded-[var(--radius)] bg-[var(--venue-accent)] px-3 py-1.5 text-xs font-semibold transition hover:opacity-90 disabled:opacity-50"
        style={{ color: "var(--venue-text)" }}
      >
        {mutation.isPending ? "…" : "Redeem"}
      </button>
    </div>
  );
}

// Post-redeem proof screen. Anti-screenshot-fraud design: three live
// elements (elapsed ticker + wall-clock + proof-expires countdown) all
// tick every second, so a screenshot's frozen values give staff three
// independent tells. Proof auto-expires after PROOF_WINDOW_MS so a
// screenshot reused hours later shows the locked/next state instead
// of the "Show at bar" screen. Countdown-to-next moved off this
// screen (belongs on the locked state, this moment is about THIS
// drink). Fenton 2026-09-09 screenshot-abuse concern.
const PROOF_WINDOW_MS = 5 * 60_000;

function RedeemedProof({
  name,
  redeemedAt,
  variant,
  onExpire,
}: {
  name: string;
  redeemedAt: number;
  variant: "row" | "hero";
  onExpire: () => void;
}) {
  // Native anti-screenshot guard: iOS blanks the app window via the
  // UITextField(isSecureTextEntry) trick, Android via FLAG_SECURE. Only
  // active while THIS card is on screen, released the moment the state
  // flips (Done tapped or 5-min expiry).
  useScreenshotGuard();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Math.max(0, Math.floor((now - redeemedAt) / 1000));
  const elapsedText =
    elapsed < 60 ? `${elapsed}s ago` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s ago`;
  const wallClock = new Date(now).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const expiresInMs = Math.max(0, PROOF_WINDOW_MS - (now - redeemedAt));
  const expiresMin = Math.floor(expiresInMs / 60_000);
  const expiresSec = Math.floor((expiresInMs % 60_000) / 1000);
  const expiresText = `${expiresMin}:${expiresSec.toString().padStart(2, "0")}`;

  // Auto-expire the proof state so a member who redeemed hours ago
  // can't just re-open the app to the "Show at bar" screen. Fires
  // once when the window closes; the parent flips to the locked
  // state (which reads the nextAvailableAt we're passing forward).
  useEffect(() => {
    if (expiresInMs <= 0) onExpire();
  }, [expiresInMs, onExpire]);

  const containerCls =
    variant === "hero"
      ? "flex flex-col items-center gap-3 rounded-[var(--radius)] border border-[var(--accent)]/50 bg-[var(--accent)]/15 p-6 text-center"
      : "flex flex-col items-center gap-3 rounded-[var(--radius)] border border-[var(--venue-accent)]/40 bg-[var(--venue-accent)]/10 p-5 text-center";
  const badgeBg = variant === "hero" ? "bg-[var(--accent)] text-[var(--accent-fg)]" : "bg-[var(--venue-accent)]";
  const expiresColor = expiresInMs < 60_000 ? "text-amber-500" : "text-[var(--foreground)]";

  return (
    <div className={containerCls}>
      <span className={`flex h-12 w-12 items-center justify-center rounded-full text-xl ${badgeBg}`}>
        ✓
      </span>
      <p className="text-base font-bold text-[var(--foreground)]">Redeemed: {name}</p>
      <p className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-xs text-[var(--subtle)]">
        Show this screen at the bar
      </p>

      {/* Hero countdown, from 5:00 to 0:00, dominates the card so bar
          staff can't miss it. Turns amber under 1 min for urgency. When
          it hits 0:00 the useEffect above fires onExpire(), the card
          transitions to the locked/next-available state. */}
      <div className="mt-1 flex flex-col items-center gap-1">
        <p className="text-[10px] uppercase tracking-wide text-[var(--muted)]">Valid for</p>
        <p className={`text-5xl font-bold tabular-nums leading-none ${expiresColor}`}>
          {expiresText}
        </p>
      </div>

      {/* Anti-screenshot signals: two small live tickers a bartender can
          eyeball to spot a frozen screenshot. A screenshot freezes all
          three (hero countdown + elapsed + wall clock) at the moment it
          was taken; the real screen keeps moving. */}
      <div className="flex items-center gap-4 pt-1 text-[11px] text-[var(--muted)]">
        <span className="tabular-nums">Redeemed <span className="font-semibold text-[var(--foreground)]">{elapsedText}</span></span>
        <span aria-hidden>·</span>
        <span className="tabular-nums">Now <span className="font-semibold text-[var(--foreground)]">{wallClock}</span></span>
      </div>

      <button
        onClick={onExpire}
        className="mt-2 rounded-full border border-[var(--border-default)] px-4 py-1.5 text-xs text-[var(--muted)] transition hover:bg-[var(--surface-2)] hover:text-[var(--foreground)]"
      >
        Done, drink received
      </button>
    </div>
  );
}

// Already-redeemed-this-period state, shows the same countdown.
function LockedCountdown({
  name,
  reason,
  nextAvailableAt,
  variant,
}: {
  name: string;
  reason: string;
  nextAvailableAt: string;
  variant: "row" | "hero";
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const countdownText = formatCountdown(new Date(nextAvailableAt).getTime(), now);
  const containerCls =
    variant === "hero"
      ? "flex items-center gap-3 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface-2)] p-4"
      : "flex items-center gap-3 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface-2)] p-3";
  return (
    <div className={containerCls}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--surface)] text-[var(--muted)]">
        ⏳
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[var(--foreground)]">{name}</p>
        <p className="text-xs text-[var(--muted)]">{reason}</p>
        <p className="mt-1 text-xs text-[var(--muted)]">
          Next available in{" "}
          <span className="font-semibold tabular-nums text-[var(--foreground)]">
            {countdownText}
          </span>
        </p>
      </div>
    </div>
  );
}

// White gift-icon fallback for the hero card's idle state, replacing the
// 🎁/🍺 emoji glyphs, swapped for a venue-configured benefit.iconEmoji.
function GiftIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H4.5a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
    </svg>
  );
}
